import { styled } from "goober";
import { navEnterStyles } from "../hooks/useNavTransition";
import { usePanelTransition } from "./PanelTransition";
import { queries } from "../../shared/queries";
import type { Slug } from "../../shared/slug";
import { useQuery } from "@rocicorp/zero/react";
import type { Connector } from "../lib/connector";
import type { ParsedStep } from "../lib/parseCommitData";
import { routes, useCurrentUrl, useMakeUrl } from "../lib/urls";
import { ResponsiveTabs } from "./ResponsiveTabs";
import {
  PanelHeader,
  PanelHeaderActions,
  PanelHeaderNavigation,
} from "./PanelHeader";
import { PanelFrame } from "./PanelFrame";
import { ChatpadToggleButton } from "./PersonalChats/ChatpadToggleButton";
import type { ElementSnippet } from "./Chat/chatTypes";
import type { BranchData, CommitFileData } from "./Workflow";
import { WorkflowBrowserPanel } from "./WorkflowBrowserPanel";
import { WorkflowChatConnectorsBar } from "./WorkflowChatConnectorsBar";
import { WorkflowFilesPanel } from "./WorkflowFilesPanel";
import { WorkflowLinkGallery } from "./WorkflowLinkGallery";
import { WorkflowMonitorPanel } from "./WorkflowMonitorPanel";
import { WorkflowReadmePanel } from "./WorkflowReadmePanel";
import { WorkflowRunsPanel } from "./WorkflowRunsPanel";
import { resolveWorkflowTabOrder, type Tab, VALID_TABS } from "./WorkflowTabs";

function parseTab(raw: string | undefined, fallback: Tab): Tab {
  return raw && VALID_TABS.has(raw) ? (raw as Tab) : fallback;
}

type Props = {
  onClosePanel: () => void;
  workflowId: string;
  branchId: string;
  spaceSlug: Slug;
  customHostname?: string | null;
  spaceId: string;
  branch: BranchData;
  headCommitFiles: readonly CommitFileData[];
  canWrite: boolean;
  connectorsReady: boolean;
  isReadOnly: boolean;
  readme: string | null;
  steps: ReadonlyArray<ParsedStep>;
  allConnectors: ReadonlyArray<Connector>;
  inspectMode: boolean;
  onInspectModeChange: (mode: boolean) => void;
  onElementSelect: (
    element: Omit<ElementSnippet, "stepName" | "color">
  ) => void;
  onElementDeselect: (index: number) => void;
  elementSnippets: readonly ElementSnippet[];
  hoveredCanonicalId: string | null;
  onStepHover: (canonicalId: string | null) => void;
  onStepsHover: (canonicalIds: ReadonlyArray<string> | null) => void;
  onRunsTabStepsChange: (canonicalIds: ReadonlyArray<string> | null) => void;
  onAskWriteReadme: () => void;
  onAddTests: () => void;
  onInvestigateFailingSteps: (stepNames: readonly string[]) => void;
};

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  height: 0;
  flex: 1;
`;

const BodyArea = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 12px;
`;

const BodyCard = styled(PanelFrame)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  &[data-flat="true"] {
    background: transparent;
    border: none;
    border-radius: 0;
    overflow: visible;
  }
`;

const BodyContent = styled("div")`
  ${navEnterStyles}
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
`;

export function WorkflowOverview({
  onClosePanel,
  workflowId,
  branchId,
  spaceSlug,
  customHostname,
  spaceId,
  branch,
  headCommitFiles,
  canWrite,
  isReadOnly,
  readme,
  steps,
  allConnectors,
  connectorsReady,
  inspectMode,
  onInspectModeChange,
  onElementSelect,
  onElementDeselect,
  elementSnippets,
  hoveredCanonicalId,
  onStepHover,
  onStepsHover,
  onRunsTabStepsChange,
  onAskWriteReadme,
  onAddTests,
  onInvestigateFailingSteps,
}: Props) {
  const direction = usePanelTransition();
  const { tab: rawTab } = useCurrentUrl(routes.space.workflow);
  const makeUrl = useMakeUrl();

  const [preferences] = useQuery(queries.userPreferences());
  const orderedTabs = resolveWorkflowTabOrder(preferences?.workflowTabOrder);

  const fallbackTab = readme?.trim()
    ? "readme"
    : (orderedTabs[0]?.key ?? "monitor");
  const tab = parseTab(rawTab, fallbackTab);
  const flat = tab === "browser" || tab === "monitor" || tab === "runs";

  const connectorIds = new Set<string>();
  for (const step of steps) {
    for (const connector of step.connectedApps) {
      if (connector.connectedAppId) connectorIds.add(connector.connectedAppId);
    }
  }
  const workflowConnectors = allConnectors.filter((connector) =>
    connectorIds.has(connector.id)
  );

  return (
    <Container>
      <PanelHeader>
        <PanelHeaderNavigation data-nav-enter={direction}>
          <ResponsiveTabs
            label="Workflow views"
            activeKey={tab}
            items={orderedTabs.map((item) => ({
              ...item,
              to: makeUrl(routes.space.workflow, { tab: item.key }),
            }))}
          />
        </PanelHeaderNavigation>
        <PanelHeaderActions>
          <WorkflowChatConnectorsBar
            workflowId={workflowId}
            branchId={branchId}
            spaceId={spaceId}
            canWrite={canWrite}
            availableConnectors={allConnectors}
            disabled={!connectorsReady}
          />
          <ChatpadToggleButton isOpen onToggle={onClosePanel} />
        </PanelHeaderActions>
      </PanelHeader>
      <BodyArea>
        <BodyCard data-flat={flat ? "true" : undefined}>
          <BodyContent data-nav-enter={flat ? undefined : direction}>
            {tab === "files" && (
              <WorkflowFilesPanel
                key={branchId}
                spaceId={spaceId}
                branch={branch}
                canWrite={canWrite}
              />
            )}
            {tab === "readme" && (
              <WorkflowReadmePanel
                readme={readme}
                canWrite={canWrite}
                stepNames={steps.map((s) => s.name)}
                onAskWriteReadme={onAskWriteReadme}
              />
            )}
            {tab === "links" && (
              <WorkflowLinkGallery
                isReadOnly={isReadOnly}
                workflowId={workflowId}
                branchId={branchId}
                steps={steps}
                onStepHover={onStepHover}
              />
            )}
            {tab === "browser" && (
              <WorkflowBrowserPanel
                branch={branch}
                headCommitFiles={headCommitFiles}
                spaceSlug={spaceSlug}
                customHostname={customHostname}
                spaceId={spaceId}
                steps={steps}
                inspectMode={inspectMode}
                onInspectModeChange={onInspectModeChange}
                onElementSelect={onElementSelect}
                onElementDeselect={onElementDeselect}
                elementSnippets={elementSnippets}
              />
            )}
            {tab === "monitor" && (
              <WorkflowMonitorPanel
                canWrite={canWrite}
                workflowId={workflowId}
                branchId={branchId}
                steps={steps}
                connectors={workflowConnectors}
                onStepsHover={onStepsHover}
                onAddTests={onAddTests}
                onInvestigateFailingSteps={onInvestigateFailingSteps}
              />
            )}
            {tab === "runs" && (
              <WorkflowRunsPanel
                workflowId={workflowId}
                branchId={branchId}
                steps={steps}
                hoveredCanonicalId={hoveredCanonicalId}
                onStepHover={onStepHover}
                onActiveRunStepsChange={onRunsTabStepsChange}
              />
            )}
          </BodyContent>
        </BodyCard>
      </BodyArea>
    </Container>
  );
}
